/**
 * Serviço singleton para detecção de plataforma iOS.
 *
 * Identifica dispositivos iOS (iPhone, iPad, iPod) incluindo iPads com
 * iOS 13+ que reportam user agent de desktop (MacIntel com touch).
 * Também detecta se o PWA está em modo standalone (instalado na tela inicial).
 *
 * Segue o padrão singleton dos demais serviços do projeto (alertService, authService, etc).
 * A detecção é stateless e reutilizável em qualquer contexto — componentes, mixins, services.
 */
class IOSDetectorService {
  /**
   * Verifica se o dispositivo atual é iOS.
   *
   * Realiza a detecção através de duas estratégias complementares:
   * 1. Verifica se o user agent contém "iPhone", "iPad" ou "iPod" (case-insensitive)
   * 2. Verifica se platform é "MacIntel" com maxTouchPoints > 0 (iPad iOS 13+
   *    que reporta user agent de desktop)
   *
   * Trata fallbacks quando APIs do navigator estiverem indisponíveis,
   * retornando false como valor seguro.
   *
   * @returns {boolean} true se o dispositivo for iOS, false caso contrário
   */
  isIOS () {
    try {
      const userAgent = navigator.userAgent || ''
      const iosRegex = /iPhone|iPad|iPod/i

      if (iosRegex.test(userAgent)) {
        return true
      }

      // iPad com iOS 13+ reporta user agent de desktop (MacIntel)
      // mas possui maxTouchPoints > 0, diferenciando de Macs reais
      const platform = navigator.platform || ''
      const maxTouchPoints = navigator.maxTouchPoints || 0

      if (platform === 'MacIntel' && maxTouchPoints > 0) {
        return true
      }

      return false
    } catch {
      return false
    }
  }

  /**
   * Verifica se o PWA está em modo standalone (instalado na tela inicial).
   *
   * Realiza a detecção através de duas estratégias complementares:
   * 1. Verifica navigator.standalone === true (propriedade específica do Safari/iOS)
   * 2. Verifica se a media query (display-mode: standalone) é satisfeita
   *
   * Trata fallbacks quando APIs do navigator ou window.matchMedia
   * estiverem indisponíveis, retornando false como valor seguro.
   *
   * @returns {boolean} true se o PWA estiver em modo standalone, false caso contrário
   */
  isStandalone () {
    try {
      // Propriedade específica do Safari/iOS para detectar modo standalone
      if (navigator.standalone === true) {
        return true
      }

      // Fallback via media query para outros navegadores
      if (typeof window.matchMedia === 'function') {
        const standaloneQuery = window.matchMedia('(display-mode: standalone)')
        if (standaloneQuery.matches) {
          return true
        }
      }

      return false
    } catch {
      return false
    }
  }
}

export const iosDetectorService = new IOSDetectorService()
